import { useMemo } from 'react';
import {
  RESIDENCY_HELPER,
  RESIDENCY_LABEL,
  consequenceLine,
  countryOptions,
  createdInZoneNotice,
  zoneFor,
  type ZoneMap,
} from '../obw/residency';
import { ZONES, consoleZoneLabel, type PortalZone } from '../lib/zones';
import { useZoneWriteReport } from './ZoneContext';

/**
 * ResidencyCountryField — the CUSTOMER's country on a staff create form (ADR-124 D5, I5, I9).
 *
 * ⭐⭐ IT REPLACES THE "CREATE IN ZONE" PICKER (PR #486 / Track 7), AND THE DIFFERENCE IS THE POINT.
 * The picker asked staff to choose a datacentre. This asks the question the customer answers on
 * the public form — which country the business is registered in — and the zone FOLLOWS from it
 * through the served map, exactly as on self-serve. So a UK business is created in the US zone
 * whichever zone the staff member is working in, and the console stops being the one place
 * residency is expressed as an AWS region.
 *
 * What it shows, in order: the I9 question and helper; the customer-facing consequence line; the
 * derived zone REGION-FIRST (the console's label — ADR-123 Decision 4); and, when that zone is
 * not the staff member's working zone, I9's notice "This account will be created in {Zone}."
 * before anything is submitted. While on screen it reports the derived zone to the navigation
 * bar's write indicator, as every create target does.
 *
 * ⚠ A DERIVED ZONE THIS BUNDLE CANNOT REACH IS REPORTED, NOT SUBSTITUTED. `onResolved` hands the
 * caller `null` for it, and the caller refuses to submit — never quietly creating in another zone.
 */
export function ResidencyCountryField({
  id,
  value,
  onChange,
  map,
  workingZone,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (code: string) => void;
  map: ZoneMap | null | undefined;
  /** The console's working zone (ADR-123 D3 precedence) — for the "created in another zone" notice. */
  workingZone: PortalZone;
  disabled?: boolean;
}) {
  const options = useMemo(() => (map ? countryOptions(map) : []), [map]);
  const derived = value && map ? zoneFor(map, value) : null;
  useZoneWriteReport(derived?.zone ?? null);
  const reachable = derived ? resolveCreateZone(derived.zone) : null;

  return (
    <label className="portal-uat-field-label residency-country-field">
      <span>{RESIDENCY_LABEL}</span>
      <select
        id={id}
        className="portal-select"
        value={value}
        disabled={disabled || !map}
        aria-describedby={`${id}-hint`}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— Select —</option>
        {options.map((o) => (
          <option key={o.code} value={o.code}>{o.name}</option>
        ))}
      </select>
      <span id={`${id}-hint`} className="residency-country-hint">{RESIDENCY_HELPER}</span>
      {map === null && (
        <span className="portal-error" role="alert">Something went wrong on our side. Please try again in a moment.</span>
      )}
      {derived && map && (
        <>
          <span className="residency-country-consequence" data-testid={`${id}-consequence`}>
            {consequenceLine(map, value)}
          </span>
          <span className="residency-country-zone" data-testid={`${id}-zone`}>
            Zone: {consoleZoneLabel(derived.zone)}
          </span>
          {derived.zone !== workingZone.code && (
            <span className="residency-country-notice" role="note" data-testid={`${id}-notice`}>
              {createdInZoneNotice(derived.label)}
            </span>
          )}
          {!reachable && (
            <span className="portal-error" role="alert" data-testid={`${id}-unreachable`}>
              This console isn’t configured for that zone yet. Please contact platform support.
            </span>
          )}
        </>
      )}
    </label>
  );
}

/** The PortalZone a derived zone code creates in — or null when this bundle cannot reach it. */
export function resolveCreateZone(code: string | null | undefined): PortalZone | null {
  return (code ? ZONES.find((z) => z.code === code) : undefined) ?? null;
}
